<template>
  <div class="language-selector">
    <!-- Icon-only/Secondary variants -->
    <div
      v-if="variant === 'icon-only' || variant === 'secondary'"
      class="hs-dropdown w-full relative"
    >
      <button
        type="button"
        class="hs-dropdown-toggle inline-flex items-center justify-center rounded-lg border py-3 px-2 shadow-xs focus:outline-none disabled:opacity-50 disabled:pointer-events-none transition-all"
        @click="toggleDropdown"
        :class="[
          variant === 'secondary'
            ? 'bg-gray-50 border-gray-200 hover:bg-gray-100'
            : 'bg-secondary-50 border-gray-200 hover:bg-gray-50',
          sizeClasses,
          customClass
        ]"
        :aria-expanded="dropdownOpen"
      >
        <component v-if="currentFlagIcon" :is="currentFlagIcon" class="size-5" />
        <ChevronDown class="ml-1 size-3 hs-dropdown-open:rotate-180 transition-transform" />
      </button>

      <!-- Dropdown Menu -->
      <div
        class="hs-dropdown-menu transition-[opacity,margin] duration hs-dropdown-open:opacity-100 opacity-0 hidden min-w-48 bg-white shadow-md rounded-lg mt-2 p-1 space-y-0.5 z-10"
        aria-labelledby="language-select-button"
      >
        <button
          v-for="option in languageOptions"
          :key="option.value"
          @click="handleLanguageChange(option.value)"
          class="w-full flex items-center gap-x-2 py-2 px-3 rounded-lg text-sm text-gray-800 hover:bg-gray-100 focus:outline-none"
          :class="{ 'bg-gray-100 text-primary': selectedLanguage === option.value }"
          type="button"
        >
          <component v-if="option.icon" :is="option.icon" class="flex-shrink-0 size-4" />
          <span>{{ option.label }}</span>
        </button>
      </div>
    </div>

    <!-- Regular variants -->
    <BaseSelect
      v-else
      v-model="selectedLanguage"
      :options="languageOptions"
      :placeholder="$t('placeholders.selectLanguage')"
      :variant="mapVariantForBaseSelect"
      :class="[sizeClasses, customClass]"
      @update:model-value="handleLanguageChange"
    />
  </div>
</template>

<script setup lang="ts">
import BaseSelect from '@/components/ui/BaseSelect.vue'
import EnFlag from '@/components/icons/EnFlag.vue'
import RuFlag from '@/components/icons/RuFlag.vue'
import ChFlag from '@/components/icons/ChFlag.vue'
import { ChevronDown } from 'lucide-vue-next'
import { useI18n, type SupportedLocale, isSupportedLocale } from '@/composables/useI18n'
import { computed, ref } from 'vue'

interface LanguageSelectorProps {
  /**
   * Visual variant of the selector
   * - icon-only: Only flag icon in button, full labels in dropdown
   * - secondary: Secondary button style (like btn-secondary), icon-only
   * - compact: Minimal size for headers/toolbars
   * - default: Standard size for forms
   * - outline: Outlined version
   */
  variant?: 'icon-only' | 'compact' | 'default' | 'outline' | 'secondary'

  /**
   * Size of the selector
   */
  size?: 'sm' | 'md' | 'lg'

  /**
   * Custom CSS class
   */
  customClass?: string
}

const props = withDefaults(defineProps<LanguageSelectorProps>(), {
  variant: 'default',
  size: 'md',
  customClass: ''
})

// Emit events for parent component
const emit = defineEmits<{
  (e: 'language-changed', language: string): void
}>()

const { currentLocale, availableLanguages, setLocale } = useI18n()

// Reactive state for dropdown open/close
const dropdownOpen = ref(false)

// Reactive selected language
const selectedLanguage = computed({
  get: () => currentLocale.value,
  set: (value: SupportedLocale) => setLocale(value)
})

// Flag icon lookup map
const flagIconMap = {
  en: EnFlag,
  ru: RuFlag,
  zh: ChFlag
} as const

// Language options with flags - dynamically generated from availableLanguages
const languageOptions = computed(() =>
  availableLanguages.map((lang) => ({
    value: lang.value,
    label: lang.nativeLabel || lang.label,
    icon: flagIconMap[lang.value] || EnFlag // fallback to English flag if no icon found
  }))
)

// Current flag icon for icon-only variant
const currentFlagIcon = computed(() => {
  const currentOption = languageOptions.value.find(
    (option) => option.value === selectedLanguage.value
  )
  return currentOption?.icon || EnFlag
})

// Size classes based on props
const sizeClasses = computed(() => {
  switch (props.size) {
    case 'sm':
      return 'text-xs'
    case 'lg':
      return 'text-base'
    default:
      return 'text-sm'
  }
})

// Map LanguageSelector variants to BaseSelect compatible variants
const mapVariantForBaseSelect = computed(() => {
  switch (props.variant) {
    case 'compact':
      return 'default'
    case 'outline':
      return 'outline'
    default:
      return 'default'
  }
})

// Toggle dropdown state
function toggleDropdown() {
  dropdownOpen.value = !dropdownOpen.value
}

// Handle language change
function handleLanguageChange(value: string | number) {
  // Convert to string if it's a number
  const stringValue = String(value)

  // Validate that the value is a supported locale
  if (!isSupportedLocale(stringValue)) {
    console.warn(`Invalid locale value: ${stringValue}. Supported locales are: en, ru, zh`)
    return // Ignore the invalid change
  }

  const locale = stringValue // TypeScript now knows this is SupportedLocale
  selectedLanguage.value = locale
  dropdownOpen.value = false // Close dropdown after selection
  emit('language-changed', locale)
}
</script>